/**
 * PriceHoursEditor — 7×24 grid of price multipliers (day × hour).
 * Admin clicks a cell to edit its coefficient (default 1.0 = no change).
 * Heat-map colors: <1 green (discount), =1 neutral, >1 amber/red (surcharge).
 *
 * The multiplier is applied SILENTLY on the backend — customer never sees it.
 */
import React, { useEffect, useState } from 'react';
import { Clock, Save, Loader2, Power, RotateCcw, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const blankMatrix = () => Array.from({ length: 7 }, () => Array(24).fill(1.0));

function cellTone(v) {
  if (v === 1.0) return 'bg-slate-50 text-slate-400';
  if (v < 1.0) {
    // discount — green
    if (v <= 0.5) return 'bg-emerald-200 text-emerald-900 font-bold';
    if (v <= 0.8) return 'bg-emerald-100 text-emerald-800 font-bold';
    return 'bg-emerald-50 text-emerald-700 font-semibold';
  }
  // surcharge — amber/red
  if (v >= 2.0) return 'bg-red-200 text-red-900 font-bold';
  if (v >= 1.5) return 'bg-amber-200 text-amber-900 font-bold';
  return 'bg-amber-100 text-amber-800 font-semibold';
}

export default function PriceHoursEditor({ token }) {
  const [enabled, setEnabled] = useState(false);
  const [tz, setTz] = useState(3);
  const [matrix, setMatrix] = useState(blankMatrix());
  const [editing, setEditing] = useState(null); // [day, hour]
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API_URL}/api/service/price-hours`);
      const j = await r.json();
      setEnabled(!!j.enabled);
      setTz(Number(j.timezone_offset_hours ?? 3));
      setMatrix((j.matrix && j.matrix.length === 7) ? j.matrix : blankMatrix());
      setLoaded(true);
    })();
  }, []);

  const openEdit = (d, h) => {
    setEditing([d, h]);
    setEditingValue(String(matrix[d][h]));
  };
  const commitEdit = () => {
    if (!editing) return;
    const [d, h] = editing;
    const v = parseFloat(editingValue);
    if (!isNaN(v) && v >= 0) {
      const m = matrix.map(r => [...r]);
      m[d][h] = Math.round(v * 100) / 100;
      setMatrix(m);
    }
    setEditing(null); setEditingValue('');
  };
  const resetCell = (d, h) => {
    const m = matrix.map(r => [...r]); m[d][h] = 1.0; setMatrix(m);
  };
  const resetAll = () => {
    if (!window.confirm('Reset ALL cells to 1.0?')) return;
    setMatrix(blankMatrix());
  };
  const fillRow = (d) => {
    const v = prompt(`Set coefficient for all 24 hours of ${DAY_LABELS[d]} (current row). Enter a number (e.g., 1.5):`);
    const val = parseFloat(v);
    if (isNaN(val) || val < 0) return;
    const m = matrix.map(r => [...r]);
    m[d] = Array(24).fill(Math.round(val * 100) / 100);
    setMatrix(m);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/service/price-hours`, {
        method: 'PUT', headers, body: JSON.stringify({
          enabled, timezone_offset_hours: tz, matrix,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setMatrix(j.matrix || matrix);
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="p-6 text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Loading price matrix…</div>;

  return (
    <div className="space-y-4" data-testid="price-hours-editor">
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Silent price multiplier by day & hour</p>
          <p>Multiplier applied on top of base prices before the customer sees them. <strong>Customers never see why the price changed.</strong> Default 1.0 = no change. Use &gt;1 for surcharge (e.g. 1.5 = +50%), &lt;1 for discount.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <button onClick={() => setEnabled((v) => !v)} data-testid="price-hours-toggle"
          className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 ${enabled ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
          <Power className="w-4 h-4" /> {enabled ? 'ON (coefficients applied)' : 'OFF (all prices normal)'}
        </button>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">Timezone offset (UTC):</span>
          <input type="number" value={tz} onChange={(e) => setTz(Number(e.target.value))}
            data-testid="price-hours-tz"
            className="w-16 px-2 py-1 border border-slate-300 rounded text-sm text-center" />
        </label>
        <button onClick={resetAll} data-testid="price-hours-reset-all"
          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reset all to 1.0
        </button>
        <button onClick={save} disabled={saving} data-testid="price-hours-save"
          className="ml-auto px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save matrix
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto p-3">
        <table className="text-xs border-collapse" data-testid="price-hours-grid">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white z-10 px-2 py-2 text-left text-slate-400 uppercase text-[10px]">Day</th>
              {Array.from({ length: 24 }).map((_, h) => (
                <th key={h} className="px-1 py-1 text-center text-slate-500 font-mono w-10">{String(h).padStart(2, '0')}</th>
              ))}
              <th className="px-2 py-1 text-center text-slate-400 text-[10px]">Fill</th>
            </tr>
          </thead>
          <tbody>
            {DAY_LABELS.map((d, di) => (
              <tr key={di}>
                <td className="sticky left-0 bg-white z-10 px-2 py-1 text-xs font-bold text-slate-700">{d}</td>
                {matrix[di].map((v, h) => {
                  const isEditing = editing && editing[0] === di && editing[1] === h;
                  return (
                    <td key={h} className="p-0">
                      {isEditing ? (
                        <input autoFocus type="number" step="0.05" min="0"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => e.key === 'Enter' && commitEdit()}
                          data-testid={`price-cell-edit-${di}-${h}`}
                          className="w-10 h-7 text-center border-2 border-cyan-500 rounded text-xs" />
                      ) : (
                        <button onClick={() => openEdit(di, h)}
                          onContextMenu={(e) => { e.preventDefault(); resetCell(di, h); }}
                          title={`Day ${d}, Hour ${h}:00 — coef ${v} (right-click to reset)`}
                          data-testid={`price-cell-${di}-${h}`}
                          className={`w-10 h-7 text-xs rounded ${cellTone(v)} hover:ring-2 hover:ring-cyan-400`}>
                          {v === 1.0 ? '·' : v}
                        </button>
                      )}
                    </td>
                  );
                })}
                <td className="px-1 text-center">
                  <button onClick={() => fillRow(di)} title={`Fill entire ${d} with same value`}
                    data-testid={`price-row-fill-${di}`}
                    className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 rounded text-[10px]">fill</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-4">
        <span>Legend:</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-emerald-100 border border-emerald-200"></span>discount (&lt;1)</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-slate-50 border border-slate-200"></span>no change (=1)</span>
        <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-amber-100 border border-amber-200"></span>surcharge (&gt;1)</span>
        <span className="ml-auto italic">Tip: click a cell to edit, right-click to reset to 1.0, "fill" to set entire row.</span>
      </div>
    </div>
  );
}
